/**
 * noteAuthor.tsx — WHO wrote a clinical note, and what they were qualified to
 * write (CTO clinical-governance review, 2026-08-27).
 *
 * WHY THIS EXISTS. Both clinical-note panels (AssessmentNotes.tsx —
 * pre-checkout, keyed by assessmentId; CaseNotes.tsx — per OrderCase) render
 * under the heading "Clinical notes", and both showed the author's NAME with
 * no role. The compose route WAS @Roles('CLINICIAN', 'ADMIN') in
 * clinic-service (assessment-notes.controller.ts), so an admin's note and a
 * prescribing clinician's note were indistinguishable in the clinical record.
 * This module makes the distinction visible. It is ADDITIVE and REVERSIBLE:
 * it removes nobody's capability and gates nothing.
 *
 * UPDATE 2026-08-28. The CTO withdrew the ADMIN grant: both write routes are
 * @Roles('CLINICIAN') and the portal hides the composer and the Edit link
 * from admins. This module is UNCHANGED and still needed — it labels the
 * notes admins already wrote, which do not disappear because the rule moved.
 * Deriving the qualification from identity rather than stamping a role column
 * on new rows is what makes those historical notes legible at all; a column
 * would have left every pre-migration note blank, including the exact ones
 * this now identifies.
 *
 * WHERE THE ROLE COMES FROM, and why it is derived rather than read.
 * The note wire cannot answer this: `assessmentNote` / `CaseNote` are
 * `.strict()` with `{noteId, authorUserId, authorEmail?, body, createdAt,
 * editedAt?, questionKey, patientVisible}` — no role — and the AssessmentNote
 * row does not store one either, so even adding the field would leave every
 * note written before the migration unlabelled. R19 also keeps actor names and
 * roles off audit/event payloads by design ("no actor names are resolved
 * server-side — opaque ids only"). The author's QUALIFICATION, though, is a
 * property of the person and is already on the wire: identity's clinician
 * record (GET /v1/identity/clinician/records/:userId) exists for a registered
 * clinician and 404s for anyone else. Deriving from it labels historical notes
 * correctly too, which a new column never could.
 *
 * WHAT IS DELIBERATELY *NOT* CLAIMED. A 404 proves "identity holds no
 * clinician record", NOT "this person is an admin" — so the negative case says
 * exactly that and never names a role it cannot evidence. A transport/5xx
 * failure is a THIRD state ("unconfirmed"), never folded into the negative
 * one: on a clinical record, silently downgrading a real clinician because the
 * identity service blinked would be its own falsehood. The positive claim is
 * the only one backed by a 200, which is the fail-safe direction — this panel
 * can under-claim a qualification, never over-claim one.
 *
 * The role LABEL is plain text in a Badge, so state never rides on colour
 * alone (WCAG 1.4.1, the repo's chip rule); the tone and the `title` only add
 * detail to text that already says it.
 */
import { useCallback, useState } from 'react';
import { ApiError } from '../api/client';
import { getClinicianRecord } from '../api/identity';
import type { CaseNote, PrescriberType } from '../api/types';
import { Badge } from '../ui/kit';

/**
 * What we were able to ESTABLISH about a note's author. Three states, because
 * "no clinician record" and "could not ask" are different facts and only one
 * of them says anything about the person.
 */
export type AuthorQualification =
  /** Identity returned a clinician record (200) — a registered clinician. */
  | 'clinician'
  /** Identity answered 404 — it holds no clinician record for this author. */
  | 'non_clinician'
  /** The lookup did not complete (offline, 5xx, 403) — nothing is claimed. */
  | 'unknown';

/** One author's resolved identity: the display name plus what backs it. */
export interface NoteAuthor {
  /** Identity's stored full name; undefined unless a clinician record resolved. */
  name?: string;
  qualification: AuthorQualification;
  /** GMC/GPhC prescriber type off the clinician record, when it carries one. */
  prescriberType?: PrescriberType | null;
  registrationNumber?: string | null;
}

/** authorUserId → what we established about them. */
export type NoteAuthorMap = Record<string, NoteAuthor>;

/** Human wording for the qualification actually recorded on the record. */
const PRESCRIBER_LABEL: Record<PrescriberType, string> = {
  doctor: 'Doctor',
  independent_prescriber: 'Independent prescriber',
  pharmacist: 'Pharmacist',
};

/**
 * Resolve the authors of a note list to `{name, qualification}`, one identity
 * read per DISTINCT author id — the SAME per-id fan-out both panels already
 * did, on the same endpoint. This adds no network calls; it just stops
 * throwing the outcome away. (A reload after the composer posts re-reads each
 * author, exactly as it did before this change.)
 */
export function useNoteAuthors(): {
  authors: NoteAuthorMap;
  resolveAuthors: (items: readonly CaseNote[]) => void;
} {
  const [authors, setAuthors] = useState<NoteAuthorMap>({});

  const resolveAuthors = useCallback((items: readonly CaseNote[]) => {
    const ids = Array.from(new Set(items.map((n) => n.authorUserId)));
    for (const id of ids) {
      getClinicianRecord(id)
        .then((record) =>
          setAuthors((prev) => ({
            ...prev,
            [id]: {
              name: record.fullName,
              qualification: 'clinician',
              prescriberType: record.prescriberType ?? null,
              registrationNumber: record.registrationNumber ?? null,
            },
          })),
        )
        .catch((err: unknown) =>
          setAuthors((prev) => ({
            ...prev,
            // A 404 is identity ANSWERING ("no clinician record for this id");
            // anything else means we never got an answer. Only the first is
            // evidence about the author — see the file doc.
            [id]: {
              qualification: err instanceof ApiError && err.status === 404 ? 'non_clinician' : 'unknown',
            },
          })),
        );
    }
  }, []);

  return { authors, resolveAuthors };
}

/** The badge copy + tone + hover detail for each established finding. */
function roleChip(author: NoteAuthor): { label: string; tone: 'positive' | 'pending' | 'muted'; title: string } {
  if (author.qualification === 'clinician') {
    // The qualification DETAIL rides the title, not the row: the CTO's other
    // finding this round was chips growing these cards a line.
    const detail = [
      author.prescriberType ? PRESCRIBER_LABEL[author.prescriberType] : null,
      author.registrationNumber ? `reg ${author.registrationNumber}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
    return {
      label: 'Clinician',
      tone: 'positive',
      title: detail ? `Registered clinician — ${detail}` : 'Registered clinician (clinician record on file)',
    };
  }
  if (author.qualification === 'non_clinician') {
    return {
      // Deliberately the EVIDENCE, not a conclusion about the person. "Admin"
      // would be a guess (identity does not return a role on this read), and
      // even "Non-clinician" asserts something a 404 does not strictly prove —
      // a CLINICIAN invite has required registrationNumber + prescriberType
      // since v0.6.4, so every clinician account made through the supported
      // flow HAS a record, but a legacy account predating that would read as a
      // flat denial of someone's qualification on a clinical record. This
      // wording distinguishes the note just as sharply while claiming only
      // what was actually observed.
      label: 'No clinician record',
      tone: 'pending',
      title: 'Identity holds no clinician record for this author, so this note is not evidenced as a registered clinician\u2019s.',
    };
  }
  return {
    label: 'Role unconfirmed',
    tone: 'muted',
    title: "This author's clinician record could not be checked, so their role is not confirmed here.",
  };
}

/**
 * The author line of one note row: the display name (identity's when it
 * resolved, then the wire email, then the raw id — the established fallback
 * chain) followed by the role the record supports.
 */
export function NoteAuthorLine({ note, author }: { note: CaseNote; author?: NoteAuthor }) {
  const name = author?.name ?? note.authorEmail ?? note.authorUserId;
  // NO entry yet = the lookup is still in flight, which is not one of the
  // three findings — every id lands on one of them, including on failure. Show
  // no chip until it does: flashing "Role unconfirmed" on every open would
  // train staff to ignore the label that matters when it is real.
  if (!author) return <>{name}</>;
  const chip = roleChip(author);
  return (
    <>
      {name}{' '}
      <span title={chip.title} style={{ marginLeft: '2px', verticalAlign: 'middle' }}>
        <Badge tone={chip.tone}>{chip.label}</Badge>
      </span>
    </>
  );
}
